import { useState, useEffect, useMemo, useRef } from 'react';
import { formatDateToMDY } from '../../../constants/utils';
import {
  EXPIRY_DATE,
  LAST_INSTALLATION_DATE,
  LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
} from '../constants';
import { getExpiryDate } from '../service/CreatePromotionService';

const useDiscountOffer = (
  defaultExpiryDate,
  promotionData,
  setPromotionData,
  mode,
  loadedPromotionData,
  offersList = [],
  fetchHeadlineData
) => {
  const [disableConfig, setDisableConfig] = useState(true);
  const [configureHeadline, setConfigureHeadline] = useState(false);
  const [headlineOfferValues, setHeadlineOfferValues] = useState({});
  const [valuesForD1, setValuesForD1] = useState([]);
  const [valuesForD2, setValuesForD2] = useState([]);
  const [valuesForD3, setValuesForD3] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [promoExpiry, setPromoExpiry] = useState({});
  const [showDatePickerForRegOfferID, setShowDatePickerForRegOfferID] = useState(1);
  const [expiryDatesObj, setExpiryDatesObj] = useState([{}]);
  const [offerApplicable, setOfferApplicable] = useState(false);
  const [selectHeadline, setSelectHeadline] = useState('none');
  const [customHeadline, setCustomHeadline] = useState('');
  const [customDisclaimer, setCustomDisclaimer] = useState('');
  const [isRequiredChecked, setIsRequiredChecked] = useState(false);
  const [requiredValue, setRequiredValue] = useState('UNITS');
  const [minRequiredTextFieldValue, setMinRequiredTextFieldValue] = useState('');
  const [excludedProducts, setExcludedProducts] = useState([]);
  const valuesLoadedInitially = useRef(mode !== 'Edit');

  // Load initial data from promotionData when in Edit mode
  useEffect(() => {
    if (
      promotionData &&
      Object.keys(promotionData).length !== 0 &&
      loadedPromotionData &&
      !valuesLoadedInitially.current &&
      offersList.length > 0
    ) {
      const { discount_offer } = promotionData;

      if (discount_offer && Object.keys(discount_offer).length !== 0) {
        setOfferApplicable(true);
        let headlineSelection = 'none';

        if (discount_offer.custom) {
          headlineSelection = 'custom_offer';
        } else if (discount_offer.headline_builder_id) {
          headlineSelection = discount_offer.headline_builder_id;
        }
        setSelectHeadline(headlineSelection);
        setDisableConfig(
          !(discount_offer.headline_builder_id !== undefined || discount_offer.custom === true)
        );
        setCustomHeadline(discount_offer.custom ? discount_offer.headline : '');
        setCustomDisclaimer(discount_offer.custom ? discount_offer.disclaimer : '');
        setIsRequiredChecked(discount_offer.minimum_purchase?.required || false);
        setRequiredValue(discount_offer.minimum_purchase?.type || 'UNITS');
        setMinRequiredTextFieldValue(discount_offer.minimum_purchase?.value || '');
        setExcludedProducts(discount_offer.excluded_products || []);
        setExpiryDatesObj(
          discount_offer.offer_expiry !== undefined
            ? getExpiryDate(discount_offer.offer_expiry)
            : []
        );
        if (discount_offer?.offer_expiry?.offer_expiry_type) {
          if (discount_offer?.offer_expiry?.offer_expiry_type === EXPIRY_DATE) {
            setPromoExpiry({ EXPIRY_DATE: discount_offer?.offer_expiry?.expiry_dates });
            setShowDatePickerForRegOfferID(1);
          } else if (discount_offer?.offer_expiry?.offer_expiry_type === LAST_INSTALLATION_DATE) {
            setPromoExpiry({
              LAST_INSTALLATION_DATE: discount_offer?.offer_expiry?.expiry_dates,
            });
            setShowDatePickerForRegOfferID(2);
          } else if (
            discount_offer?.offer_expiry?.offer_expiry_type ===
            LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE
          ) {
            setPromoExpiry({
              LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE:
                discount_offer?.offer_expiry?.expiry_dates,
            });
            setShowDatePickerForRegOfferID(3);
          }
        }
      }
      valuesLoadedInitially.current = true;
    }
  }, [promotionData, loadedPromotionData, offersList]);

  // Format offers data for dropdown
  const modifiedOfferData = useMemo(() => {
    const customOffer = {
      label: 'Custom Offer (Click Configure button to set value)',
      value: 'custom_offer',
    };

    const noOffer = {
      label: 'None',
      value: 'none',
      id: 'none-headline',
    };

    let allOffers = [];

    if (Array.isArray(offersList)) {
      allOffers = offersList.map((eachOffer) => ({
        label: eachOffer.template,
        value: eachOffer.id,
        ...eachOffer,
      }));
    }
    return [noOffer, ...allOffers, customOffer];
  }, [offersList]);

  // Handler functions
  const handleOfferApplicable = (event) => {
    if (!event.target.checked) {
      setOfferApplicable(false);
      setSelectHeadline('none');
      setDisableConfig(true);
      setRequiredValue('UNITS');
      setIsRequiredChecked(false);
      setExcludedProducts([]);
      setCustomHeadline('');
      setCustomDisclaimer('');
      setMinRequiredTextFieldValue('');
      setPromoExpiry({});
      setExpiryDatesObj({});

      // Create a new object without the discount_offer property
      const updatedPromotionData = { ...promotionData };

      delete updatedPromotionData.discount_offer;
      if (!updatedPromotionData?.finance_offer?.length) {
        delete updatedPromotionData.sweetener_offer;
      }

      // Update the promotion data
      setPromotionData(updatedPromotionData);

      fetchHeadlineData(updatedPromotionData);
    } else {
      setOfferApplicable(true);
      setPromotionData({
        ...promotionData,
        discount_offer: {},
      });
    }
  };

  const handleHeadlineChange = (value) => {
    setSelectHeadline(value);
    setVariableValues({});
    setHeadlineOfferValues({});
    setCustomHeadline('');
    setCustomDisclaimer('');
    delete promotionData?.discount_offer?.custom_values;
    if (promoExpiry !== undefined && Object.keys(promoExpiry).length === 0) {
      setShowDatePickerForRegOfferID(1);
      setPromotionData({
        ...promotionData,
        discount_offer: {
          ...promotionData.discount_offer,
          headline: value === 'custom_offer' ? '' : value,
          ...(value !== 'custom_offer' && { headline_builder_id: value }),
          custom: value === 'custom_offer',
        },
      });
    } else {
      setPromotionData({
        ...promotionData,
        discount_offer: {
          ...promotionData.discount_offer,
          headline: value === 'custom_offer' ? '' : value,
          ...(value !== 'custom_offer' && { headline_builder_id: value }),
          custom: value === 'custom_offer',
        },
      });
    }
    updateDropdownValues(value);
    setDisableConfig(false);
    setConfigureHeadline(false);
  };

  const handleCustomHeadline = (value) => {
    setCustomHeadline(value);
    setPromotionData({
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        headline: value,
      },
    });
  };

  const handleCustomDisclaimer = (value) => {
    setCustomDisclaimer(value);
    setPromotionData({
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        disclaimer: value,
      },
    });
  };

  const handleDiscountIsRequired = (event) => {
    setIsRequiredChecked(event.target.checked);
    if (event.target.checked === true) {
      setPromotionData({
        ...promotionData,
        discount_offer: {
          ...promotionData.discount_offer,
          minimum_purchase: {
            ...promotionData.discount_offer.minimum_purchase,
            required: event.target.checked,
          },
        },
      });
    } else {
      const tempObj = { ...promotionData };

      delete tempObj.discount_offer.minimum_purchase;
      setPromotionData(tempObj);
      setMinRequiredTextFieldValue('');
    }
  };

  const handleToggleChange = (event, value) => {
    setRequiredValue(value);
    setPromotionData({
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        minimum_purchase: {
          ...promotionData.discount_offer.minimum_purchase,
          type: value,
          required: true,
        },
      },
    });
  };

  const handleMinRequiredTextField = (event) => {
    if (event.target.value !== minRequiredTextFieldValue) {
      setMinRequiredTextFieldValue(event.target.value);
      if (event.target.value !== '' && event.target.value >= 0) {
        setPromotionData({
          ...promotionData,
          discount_offer: {
            ...promotionData.discount_offer,
            minimum_purchase: {
              ...promotionData.discount_offer.minimum_purchase,
              type: requiredValue,
              value: event.target.value,
              required: true,
            },
          },
        });
      } else {
        const tempObj = { ...promotionData };

        delete tempObj.discount_offer.minimum_purchase;
        setPromotionData(tempObj);
      }
    }
  };

  const handleExcludedProducts = (event) => {
    const { id, checked } = event.target;

    if (checked) {
      setExcludedProducts([...excludedProducts, id]);
    } else {
      setExcludedProducts(excludedProducts.filter((product) => product !== id));
    }
    setPromotionData({
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        excluded_products: checked
          ? [...excludedProducts, id]
          : excludedProducts.filter((product) => product !== id),
      },
    });
  };

  const filterDropdownValues = (selectedValue, part) => {
    const requiredHeadline = offersList.find((eachOffer) => eachOffer.id === selectHeadline);

    if (!requiredHeadline) {
      return;
    }

    const templateVariables = requiredHeadline.template_variables;
    const index = templateVariables.indexOf(part);

    if (requiredHeadline?.nested_values && Object.keys(requiredHeadline?.nested_values)?.length) {
      if (index === 0 && requiredHeadline?.nested_values[selectedValue]) {
        setValuesForD2(Object.keys(requiredHeadline?.nested_values[selectedValue]));
      } else if (index === 1) {
        if (
          requiredHeadline?.nested_values?.[variableValues?.[templateVariables?.[index - 1]]]?.[
            selectedValue
          ]
        ) {
          setValuesForD3(
            requiredHeadline?.nested_values[variableValues[templateVariables[index - 1]]][
              selectedValue
            ]
          );
        }
      }
    }
  };

  const handleAutocompleteChange = (value, part) => {
    const tempObj = { ...variableValues };

    tempObj[part] = value;
    setVariableValues(tempObj);
    setHeadlineOfferValues({ ...headlineOfferValues, ...tempObj });

    const newPromotionData = {
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        custom_values: {
          ...promotionData.discount_offer.custom_values,
        },
      },
    };

    if (value !== '' && value !== null && value !== undefined && value >= 0) {
      newPromotionData.discount_offer.custom_values[part] = value;
    } else {
      delete newPromotionData.discount_offer.custom_values[part];
    }

    if (Object.keys(newPromotionData.discount_offer.custom_values).length === 0) {
      delete newPromotionData.discount_offer.custom_values;
    }

    setPromotionData(newPromotionData);

    filterDropdownValues(value, part);
  };

  const updateDropdownValues = (value) => {
    if (value?.length) {
      offersList.forEach((eachEntry) => {
        if (eachEntry.id === value) {
          if (eachEntry?.nested_values && Object.keys(eachEntry?.nested_values)?.length) {
            setValuesForD1(Object.keys(eachEntry?.nested_values));
          }
          if (eachEntry?.nested_values === null) {
            setValuesForD1([]);
          }
        }
      });
    }
  };

  // Date handling functions
  const handleOfferExpiryDate = (data) => {
    if (data !== null) {
      const date = formatDateToMDY(data);

      if (promoExpiry[EXPIRY_DATE] === undefined || date !== promoExpiry[EXPIRY_DATE][0]) {
        setExpiryDatesObj([{ type: 'expiresOn', id: showDatePickerForRegOfferID, value: date }]);

        setPromotionData({
          ...promotionData,
          discount_offer: {
            ...promotionData.discount_offer,
            offer_expiry: { expiry_dates: [date], offer_expiry_type: EXPIRY_DATE },
          },
        });
        setPromoExpiry({ EXPIRY_DATE: date });
      }
    }
  };

  const handleOfferExpiryDateForInstallation = (data) => {
    const date = formatDateToMDY(data);

    if (
      promoExpiry[LAST_INSTALLATION_DATE] === undefined ||
      date !== promoExpiry[LAST_INSTALLATION_DATE][0]
    ) {
      setExpiryDatesObj([{ type: 'scheduleBefore', id: showDatePickerForRegOfferID, value: data }]);
      setPromotionData({
        ...promotionData,
        discount_offer: {
          ...promotionData.discount_offer,
          offer_expiry: {
            expiry_dates: [date],
            offer_expiry_type: LAST_INSTALLATION_DATE,
          },
        },
      });
      setPromoExpiry({ LAST_INSTALLATION_DATE: date });
    }
  };

  const handleScheduleExpDate = (data) => {
    const date = formatDateToMDY(data);

    // Ensure updatedDatesObj is always an array
    let updatedDatesObj = Array.isArray(expiryDatesObj) ? [...expiryDatesObj] : [];

    if (
      updatedDatesObj[0] !== undefined &&
      updatedDatesObj[0].offer_expiry_type !== LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE
    ) {
      updatedDatesObj = [
        {
          id: 31,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      ];
    } else {
      let found = false;

      // Use Array.prototype.forEach instead of map since we're not using the return value
      updatedDatesObj.forEach((eachDate, index) => {
        if (eachDate.id === 31) {
          updatedDatesObj[index].value = date;
          found = true;
        }
      });

      if (!found) {
        updatedDatesObj.push({
          id: 31,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        });
      }
    }

    setExpiryDatesObj(updatedDatesObj);

    const updatedDatesObjCopy = [...updatedDatesObj];

    updatedDatesObjCopy.sort((a, b) => a.id - b.id);
    const sortedDatesObj = updatedDatesObjCopy;
    const dates = sortedDatesObj
      .map((obj) => obj.value)
      .filter((value) => value !== null && value !== undefined); // Filter out null or undefined values

    setPromotionData({
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        offer_expiry: {
          expiry_dates: dates,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      },
    });

    setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: dates });
  };

  const handlePurchaseExpDate = (data) => {
    const date = formatDateToMDY(data);
    let updatedDatesObj = expiryDatesObj;

    if (
      updatedDatesObj[0] !== undefined &&
      updatedDatesObj[0].offer_expiry_type !== LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE
    ) {
      updatedDatesObj = [
        {
          id: 32,
          value: date,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      ];
    } else {
      let found = false;

      updatedDatesObj.map((eachDate, index) => {
        if (eachDate.id === 32) {
          updatedDatesObj[index].value = date;
          found = true;
        }
        return null;
      });
      if (!found) {
        updatedDatesObj = [
          ...updatedDatesObj,
          {
            id: 32,
            value: date,
            offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
          },
        ];
      }
    }

    setExpiryDatesObj(updatedDatesObj);
    const updatedDatesObjCopy = [...updatedDatesObj];

    updatedDatesObjCopy.sort((a, b) => a.id - b.id);
    const sortedDatesObj = updatedDatesObjCopy;
    const dates = sortedDatesObj
      .map((obj) => obj.value)
      .filter((value) => value !== null && value !== undefined); // Filter out null or undefined values

    setPromotionData({
      ...promotionData,
      discount_offer: {
        ...promotionData.discount_offer,
        offer_expiry: {
          expiry_dates: dates,
          offer_expiry_type: LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE,
        },
      },
    });

    setPromoExpiry({ LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE: dates });
  };

  // Helper functions for rendering
  const getOptionsForAutocomplete = (index) => {
    if (index === 0) {
      return valuesForD1.map((value) => value.toString());
    }
    if (index === 1) {
      return valuesForD2.map((value) => value.toString());
    }
    return valuesForD3.map((value) => value.toString());
  };

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Headline rendering helper function
  const renderModifiedHeadlineData = (offersList, headline) => {
    const requiredHeadline = offersList.find((eachOffer) => eachOffer.id === headline);
    const template = requiredHeadline?.template;
    const templateVariables = requiredHeadline?.template_variables;

    if (!templateVariables) {
      return { parts: [], templateVariables: null };
    }

    const pattern = new RegExp(`(${templateVariables?.map(escapeRegExp).join('|')}|\\s)`);
    const parts = template.split(pattern);

    return { parts, templateVariables };
  };

  return {
    // State
    disableConfig,
    configureHeadline,
    headlineOfferValues,
    promoExpiry,
    showDatePickerForRegOfferID,
    offerApplicable,
    selectHeadline,
    customHeadline,
    customDisclaimer,
    isRequiredChecked,
    requiredValue,
    minRequiredTextFieldValue,
    excludedProducts,

    // Data
    modifiedOfferData,

    // Handler functions
    setConfigureHeadline,
    handleOfferApplicable,
    handleHeadlineChange,
    handleCustomHeadline,
    handleCustomDisclaimer,
    handleDiscountIsRequired,
    handleToggleChange,
    handleMinRequiredTextField,
    handleExcludedProducts,
    handleAutocompleteChange,
    handleOfferExpiryDate,
    handleOfferExpiryDateForInstallation,
    handleScheduleExpDate,
    handlePurchaseExpDate,
    setShowDatePickerForRegOfferID,

    // Helper functions
    getOptionsForAutocomplete,
    renderModifiedHeadlineData,
  };
};

export default useDiscountOffer;
